/**
 * Detects real bandwidth and adapts video quality settings dynamically
 */

interface BandwidthEstimate {
	downlink: number; // Mbps
	effectiveType: '4g' | '3g' | '2g' | 'slow-2g';
	measuredBandwidth?: number; // Real measured from downloads
}

const bandwidth = {
	estimates: [] as number[],
	lastMeasure: 0
};

/**
 * Gets connection info from Navigator API
 */
export function getConnectionInfo(): BandwidthEstimate {
	const connection =
		(navigator as any).connection ||
		(navigator as any).mozConnection ||
		(navigator as any).webkitConnection;

	if (!connection) {
		// Fallback: assume good connection
		return {
			downlink: 1.5,
			effectiveType: '4g',
			measuredBandwidth: getMeasuredBandwidth()
		};
	}

	return {
		downlink: connection.downlink || 1.5, // Mbps
		effectiveType: connection.effectiveType || '4g',
		measuredBandwidth: getMeasuredBandwidth()
	};
}

/**
 * Measures bandwidth from actual downloads
 * Should be called after fetching video segments
 */
export function measureBandwidth(
	bytesDownloaded: number,
	durationMs: number
): number {
	if (durationMs <= 0) return 0;

	// Convert to Mbps: (bytes / ms) * 1000 / (1000 * 1000)
	const mbps = (bytesDownloaded / durationMs) * 0.008;

	// Keep last 10 measurements
	bandwidth.estimates.push(mbps);
	if (bandwidth.estimates.length > 10) {
		bandwidth.estimates.shift();
	}

	bandwidth.lastMeasure = Date.now();
	return mbps;
}

/**
 * Gets average of recent bandwidth measurements
 */
export function getMeasuredBandwidth(): number {
	if (bandwidth.estimates.length === 0) return 0;

	const avg =
		bandwidth.estimates.reduce((a, b) => a + b, 0) / bandwidth.estimates.length;

	return Math.round(avg * 100) / 100; // Round to 2 decimals
}

/**
 * Returns quality constraints based on real bandwidth
 */
export function getQualityConstraints(isPreload: boolean): {
	maxHeight: number;
	bandwidthEstimate: number;
	bufferingGoal: number;
} {
	const connection = getConnectionInfo();
	const measured = getMeasuredBandwidth();

	// Use measured bandwidth if available, otherwise use Navigator estimate
	const estimatedMbps = measured > 0 ? measured : connection.downlink;

	if (isPreload) {
		// Preload: conservative with data
		return {
			maxHeight: estimatedMbps > 2 ? 1080 : estimatedMbps > 1 ? 720 : 480,
			bandwidthEstimate: Math.max(estimatedMbps * 0.7 * 1_000_000, 300_000), // 70% of estimated
			bufferingGoal: estimatedMbps > 2 ? 5 : 3 // Less buffer on slow connections
		};
	} else {
		// Active playback: prioritize quality
		return {
			maxHeight: estimatedMbps > 2.5 ? 1080 : estimatedMbps > 1.5 ? 720 : 480,
			bandwidthEstimate: Math.max(estimatedMbps * 1_000_000, 500_000),
			bufferingGoal: estimatedMbps > 2 ? 15 : estimatedMbps > 1 ? 10 : 5
		};
	}
}

/**
 * Gets detailed bandwidth info for debugging
 */
export function getBandwidthDebugInfo() {
	const connection = getConnectionInfo();
	const measured = getMeasuredBandwidth();

	return {
		navigator: {
			downlink: connection.downlink,
			effectiveType: connection.effectiveType
		},
		measured,
		recentMeasurements: bandwidth.estimates,
		lastMeasuredAt: new Date(bandwidth.lastMeasure).toISOString()
	};
}
